/**
 * The rail that TEACHES — 248px on the inset surface, three steps, each a dot, a title and what the
 * step is for.
 *
 * Three states, Semrush's: an answered step has a filled ink dot with a tick and ECHOES its answer
 * in place of its description; the current one carries a ring and a 2px INK bar on its left (never
 * the accent — the one ember on this dialog is the primary); a step still ahead is plain text.
 *
 * Every item is a button that goes to its step, which is what the specimen's `kgoto` does. Jumping
 * ahead is allowed for the same reason „Dalej" never refuses: nothing is at stake in LOOKING at a
 * later step, and only the save checks the draft.
 */

import { useTranslation } from 'react-i18next';

import { muscleGroupLabelKey } from '~/entities/exercise-library';
import { CheckGlyph } from '~/shared/ui/FormGlyphs';

import type { CreateExerciseState } from '../lib/useCreateExercise';
import type { WizardStepEcho } from '../model/wizardStep';

import styles from './CreateExerciseDialog.module.css';

/** The echo's two lines, worded. `model/` returns facts; the plural and the conjunctions are here. */
function useEchoLines(echo: WizardStepEcho): readonly [string, string] {
  const { t } = useTranslation();
  if (echo.step === 'basics') {
    return [
      echo.name === '' ? t('web:exerciseLibrary.creator.echo.unnamed') : echo.name,
      t('web:exerciseLibrary.creator.echo.typeMetrics', {
        type: t(`web:exerciseLibrary.type.${echo.exerciseType}`),
        count: echo.metricCount,
      }),
    ];
  }
  return [
    echo.primary.length === 0
      ? t('web:exerciseLibrary.creator.echo.noPrimary')
      : echo.primary.map((group) => t(muscleGroupLabelKey(group))).join(', '),
    echo.equipment.length === 0
      ? t('web:exerciseLibrary.creator.echo.noEquipment')
      : echo.equipment.join(', '),
  ];
}

function Echo({ echo }: { readonly echo: WizardStepEcho }) {
  const [first, second] = useEchoLines(echo);
  return (
    <>
      <span className={styles.stepEcho}>{first}</span>
      <span className={styles.stepEcho}>{second}</span>
    </>
  );
}

export function WizardRail({
  wizard,
  titleId,
}: {
  readonly wizard: CreateExerciseState;
  /** The dialog is described by this title, so the mode is announced with the step. */
  readonly titleId: string;
}) {
  const { t } = useTranslation();

  return (
    // `inert` while a save runs, with the step body: a jump would change what the save is sending.
    <aside className={styles.rail} inert={wizard.busy}>
      <p id={titleId} className={styles.railTitle}>{t(`web:exerciseLibrary.creator.dialogLabel.${wizard.mode}`)}</p>
      <nav className={styles.rails} aria-label={t(`web:exerciseLibrary.creator.dialogLabel.${wizard.mode}`)}>
        {wizard.steps.map((view) => (
          <button
            key={view.step}
            type="button"
            className={[
              styles.step,
              view.state === 'done' ? styles.stepDone : '',
              view.state === 'current' ? styles.stepCurrent : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-current={view.state === 'current' ? 'step' : undefined}
            onClick={() => {
              wizard.goTo(view.step);
            }}
          >
            <span className={styles.stepDot} aria-hidden>
              {view.state === 'done' ? (
                <CheckGlyph className={styles.stepDotGlyph} />
              ) : (
                view.position
              )}
            </span>
            <span>
              <span className={styles.stepTitle}>
                {t(`web:exerciseLibrary.creator.steps.${view.step}.title`)}
              </span>
              {view.echo === null ? (
                <span className={styles.stepDesc}>
                  {t(`web:exerciseLibrary.creator.steps.${view.step}.description`)}
                </span>
              ) : (
                <Echo echo={view.echo} />
              )}
            </span>
          </button>
        ))}
      </nav>
    </aside>
  );
}
